import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import { z } from "zod";
import { Effect } from "effect";
import { authMiddleware } from "~/lib/middlewares/auth-middleware";
import { groupBy } from "~/lib/utils";
import { Database } from "../database";
import {
	source,
	type GrammarContent,
	type KanjiContent,
	type Source,
	type VocabularyContent,
} from "../db/schema/flashcard-schema";

export type MaterialEntry = Pick<Source, "sid" | "gid" | "reading" | "content">;

const getSummaryByLevel = Effect.fn(function* (level: string) {
	const db = yield* Database;

	const rows = yield* Effect.tryPromise(() =>
		db
			.select({
				reading: source.reading,
				type: source.type,
			})
			.from(source)
			.where(eq(source.level, level))
			.execute(),
	);

	const vocabCount = rows.reduce(
		(acc: number, curr) => (curr.type === "vocabulary" ? acc + 1 : acc),
		0,
	);
	const grammarCount = rows.reduce(
		(acc: number, curr) => (curr.type === "grammar" ? acc + 1 : acc),
		0,
	);
	const kanjiCount = rows.reduce(
		(acc: number, curr) => (curr.type === "kanji" ? acc + 1 : acc),
		0,
	);
	return {
		vocabulary: vocabCount,
		grammar: grammarCount,
		kanji: kanjiCount,
	};
});

export const getLearningMaterials = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.handler(() =>
		Effect.gen(function* () {
			const [n5, n4, n3, n2, n1] = yield* Effect.all([
				getSummaryByLevel("n5"),
				getSummaryByLevel("n4"),
				getSummaryByLevel("n3"),
				getSummaryByLevel("n2"),
				getSummaryByLevel("n1"),
			]);
			return { n5, n4, n3, n2, n1 };
		}).pipe(
			Effect.withSpan("getLearningMaterials"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);

export const getVocabularyItem = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.validator(
		z.object({
			vocabId: z.string(),
		}),
	)
	.handler(({ data }) =>
		Effect.gen(function* () {
			const db = yield* Database;

			const [result] = yield* Effect.tryPromise(() =>
				db
					.select()
					.from(source)
					.where(eq(source.sid, data.vocabId))
					.execute(),
			);

			return result as Source & {
				content: VocabularyContent;
			};
		}).pipe(
			Effect.withSpan("getVocabularyItem"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);

export const getGrammarItem = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.validator(
		z.object({
			grammarId: z.string(),
		}),
	)
	.handler(({ data }) =>
		Effect.gen(function* () {
			const db = yield* Database;

			const [result] = yield* Effect.tryPromise(() =>
				db
					.select()
					.from(source)
					.where(eq(source.sid, data.grammarId))
					.execute(),
			);

			return result as Source & {
				content: GrammarContent;
			};
		}).pipe(
			Effect.withSpan("getGrammarItem"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);

export const getKanjiItem = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.validator(
		z.object({
			kanjiId: z.string(),
		}),
	)
	.handler(({ data }) =>
		Effect.gen(function* () {
			const db = yield* Database;

			const [result] = yield* Effect.tryPromise(() =>
				db
					.select()
					.from(source)
					.where(eq(source.sid, data.kanjiId))
					.execute(),
			);

			return result as Source & {
				content: KanjiContent;
			};
		}).pipe(
			Effect.withSpan("getKanjiItem"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);

export const getMaterialByLevelGrouped = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.validator(
		z.object({
			level: z.enum(["n5", "n4", "n3", "n2", "n1"]),
			type: z.enum(["vocabulary", "grammar", "kanji"]),
		}),
	)
	.handler(({ data }) =>
		Effect.gen(function* () {
			const db = yield* Database;

			const materials = yield* Effect.tryPromise(() =>
				db
					.select({
						sid: source.sid,
						gid: source.gid,
						reading: source.reading,
						content: source.content,
					})
					.from(source)
					.where(and(eq(source.level, data.level), eq(source.type, data.type)))
					.orderBy(source.sid) // since we use uuidv7 this is actually the order of creation
					.execute(),
			);

			const groupedEntries = groupBy(materials, "gid");

			return Object.values(groupedEntries);
		}).pipe(
			Effect.withSpan("getMaterialByLevelGrouped"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);
